import { ChangeEvent, useContext, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import { routes } from 'config/routes'
import {
	ActionsSelect,
	BookingEngineTabs,
	CardHeader,
	CardWrapper,
	ConfirmModal,
	CtaLink,
	DeleteConfirmModal,
	EmptyTable,
	FiltersBar,
	FiltersBarField,
	FormatModal,
	Header,
	SearchInput,
	Select
} from 'shared/components'
import { useDevice, useFetchInfinite, useFilter, useModal, useTableSelect } from 'shared/hooks'
import { IOption } from 'interfaces/select.interface'
import { AuthContext } from 'store/AuthContext'
import { useFetchAreas } from './queries/useFetchAreas'
import { useActionFilters } from './hooks/useActionsFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useDeleteAreaMutation } from './mutations/useDeleteAreaMutation'
import { useDeleteAreasMutation } from './mutations/useDeleteAreasMutation'
import { useExportAreasMutation } from './mutations/useExportAreasMutation'
import { TableSection } from './organisms/TableSection'

const NUMBER_PATTERN = /^\d+$/

const AreasPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleFilter } = useFilter()
	const { auth } = useContext(AuthContext)
	const [search, setSearch] = useState(searchParams.get('number') ?? '')
	const [checkedAreas, setCheckedAreas] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<number | null>(null)
	const [chosenExportFormat, setChosenExportFormat] = useState<number | null>(null)
	const { data: boothTypes, fetchNextPage: fetchNextAreaTypes } = useFetchInfinite({
		name: 'fetchAreaTypes',
		endpoint: '/booth-types/',
		enabled: !!auth.accessToken
	})
	const boothTypeOptions: IOption[] = (boothTypes?.pages ?? [])
		.flatMap((page: any) => page.results)
		.map((item: any) => ({ label: item.name, value: item.id }))
	const statusOptions = [
		{ value: 'true', label: t('Active') },
		{ value: 'false', label: t('Inactive') }
	]
	const { booths, isAreasLoading } = useFetchAreas()
	const { select } = useTableSelect({ nodes: booths?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const [actionsFilter] = useActionFilters(!checkedAreas.length)

	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()
	const {
		isOpen: isExportModalOpen,
		openModal: openExportModal,
		closeModal: closeExportModal
	} = useModal()
	const {
		isOpen: isFormatModalOpen,
		openModal: openFormatModal,
		closeModal: closeFormatModal
	} = useModal()

	const { deleteAreaMutation } = useDeleteAreaMutation({ onSettled: closeDeleteModal })

	const { deleteAreasMutation } = useDeleteAreasMutation({
		onSettled: () => {
			clearSelection()
			closeBulkDeleteModal()
		}
	})

	const { exportAreasMutation, isExportAreasLoading } = useExportAreasMutation({
		chosenFormat: chosenExportFormat,
		onSuccess: clearSelection,
		onSettled: () => {
			closeFormatModal()
			setChosenExportFormat(null)
		}
	})

	function onSelectChange(_action: any, state: any) {
		setCheckedAreas(state.ids)
	}

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.trim()

		setSearch(value)
		handleFilter({ id: 'number', value: NUMBER_PATTERN.test(value) ? value : null })
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id)
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onDelete: handleDelete })

	const handleAction = (action: string) => {
		switch (action) {
			case 'export':
				openExportModal()
				break
			case 'delete':
				openBulkDeleteModal()
				break
		}
	}

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const handleFormatChange = (data: IOption) => {
		setChosenExportFormat(data?.value?.toString() ? (data.value as number) : null)
	}

	const renderContent = () => {
		if (search && !NUMBER_PATTERN.test(search)) return <EmptyTable text={t('No results')} />
		if (isAreasLoading) return <EmptyTable isLoading />
		if (!booths?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No areas yet')} />

		return <TableSection booths={booths} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Booking engine')}
				tabs={<BookingEngineTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Areas')}
						description={t('Individual areas in the store that can be rented.')}>
						<CtaLink to={routes.areaAdd} text={t('Add area')} />
					</CardHeader>
					<FiltersBar>
						<FiltersBarField label={t('Search areas')}>
							<SearchInput
								variant="filled"
								label={t('Search areas')}
								placeholder={t('Search')}
								onChange={handleSearch}
							/>
						</FiltersBarField>
						<FiltersBarField label={t('Area type')}>
							<Select
								id="booth_type"
								variant="filled"
								placeholder={t('All')}
								aria-label={t('Area type')}
								options={boothTypeOptions}
								value={
									boothTypeOptions.find(
										(option) => String(option.value) === searchParams.get('booth_type')
									) ?? null
								}
								handleInfiniteScroll={fetchNextAreaTypes}
								onChange={(data) =>
									handleFilter({ id: 'booth_type', value: data?.value ? String(data.value) : null })
								}
							/>
						</FiltersBarField>
						<FiltersBarField label={t('Status')}>
							<Select
								id="is_active"
								variant="filled"
								placeholder={t('All')}
								aria-label={t('Status')}
								options={statusOptions}
								value={
									statusOptions.find((option) => option.value === searchParams.get('is_active')) ??
									null
								}
								onChange={(data) => handleFilter({ id: 'is_active', value: data?.value || null })}
							/>
						</FiltersBarField>
					</FiltersBar>
					{renderContent()}
				</CardWrapper>
			</div>

			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete area')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={() => deleteAreaMutation(idToBeRemoved)}>
					<p>{t('You are about to delete an area. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected areas')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={() => deleteAreasMutation(checkedAreas)}>
					<p>
						{t('You are about to delete {{ count }} areas. Are you sure you want to proceed?', {
							count: checkedAreas.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
			{isExportModalOpen && (
				<ConfirmModal
					title={t('Export areas')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>{t('You are about to export areas. Are you sure you want to proceed?')}</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={() => exportAreasMutation({ format: chosenExportFormat, ids: checkedAreas })}
					isLoading={isExportAreasLoading}
					onChange={handleFormatChange}
				/>
			)}
		</>
	)
}

export default AreasPage
